import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Globe2, Users, Fish, Leaf, Compass, Clock, Anchor } from 'lucide-react';
import { SectionHeader, PremiumCard } from './ui';
const reveal = (delay = 0) => ({
  initial: {
    opacity: 0,
    y: 24
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-60px'
  },
  transition: {
    duration: 0.6,
    delay,
    ease: 'easeOut' as const
  }
});
function LightHeader({
  label,
  title,
  subtitle
}: {
  label: string;
  title: string;
  subtitle: string;
}) {
  return <motion.div {...reveal(0)} className="flex flex-col items-center text-center gap-4 mb-14 md:mb-16" data-id="element-193" data-mp="1a7lfxq84t2fv">
      <span className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-noronha-petroleum" data-id="element-194" data-mp="1e85bwqgyy00f">
        {label}
      </span>
      <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-slate-900 tracking-tight text-balance max-w-3xl" data-id="element-195" data-mp="3uw96gx2t658w">
        {title}
      </h2>
      <p className="text-slate-500 max-w-2xl text-lg text-balance" data-id="element-196" data-mp="2xtevllz8m4bk">
        {subtitle}
      </p>
    </motion.div>;
}
export function WhyNoronha() {
  const features = [{
    icon: Shield,
    title: 'Un océano protegido',
    description: 'Aquí la naturaleza sigue marcando el ritmo. Décadas de conservación permitieron que la vida marina florezca de una manera difícil de encontrar en otros destinos del mundo.'
  }, {
    icon: Globe2,
    title: 'Entre los grandes destinos del planeta',
    description: 'Reconocido internacionalmente por su biodiversidad, visibilidad y encuentros con fauna marina en un entorno único.'
  }, {
    icon: Users,
    title: 'La isla sigue siendo la protagonista',
    description: 'El equilibrio entre conservación y turismo permite disfrutar Fernando de Noronha de una manera cada vez más difícil de encontrar: con naturaleza preservada, playas tranquilas y experiencias auténticas.'
  }, {
    icon: Fish,
    title: 'Encuentros inolvidables',
    description: 'Tortugas, rayas, tiburones de arrecife y delfines forman parte de una vida marina que convierte cada inmersión en una sorpresa.'
  }, {
    icon: Leaf,
    title: 'Un mundo aparte',
    description: 'Playas salvajes, acantilados volcánicos y paisajes preservados que hacen sentir que el tiempo avanza a otro ritmo.'
  }];
  return <section id="viaje" className="relative bg-white pt-12 pb-24 md:pt-16 md:pb-32" data-id="element-197" data-mp="3awrc6qwvk9be">
      <div className="container mx-auto px-6" data-id="element-198" data-mp="3fuj0layb9dau">
        {/* Premium highlights */}
        <LightHeader label="El Destino" title="¿Por qué Fernando de Noronha?" subtitle="Un archipiélago volcánico en medio del Atlántico donde la naturaleza sigue siendo la protagonista." data-id="element-199" data-mp="3kwrvicuqupfl" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto" data-id="element-200" data-mp="1qjil8iudizwa">
          {features.map((feature, idx) => <motion.div key={idx} {...reveal(idx * 0.08)} className="rounded-2xl border border-slate-200 bg-white p-7 md:p-9 hover:border-noronha-emerald/40 hover:shadow-[0_12px_40px_rgba(2,6,23,0.08)] hover:-translate-y-1 transition-all duration-300" data-id="element-201" data-mp="1n0jqeqnq6zxp" data-mpi={idx}>
              <div className="w-14 h-14 rounded-2xl bg-noronha-emerald/10 flex items-center justify-center mb-6" data-id="element-202" data-mp="199em3b9exjh1">
                <feature.icon className="w-7 h-7 text-noronha-emerald" strokeWidth={1.5} data-id="element-203" data-mp="25xuerg7tk8yd" />
              </div>
              <h3 className="text-xl font-heading text-slate-900 mb-3" data-id="element-204" data-mp="1wx66l253x2te">
                {feature.title}
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-205" data-mp="1rxtarrw9cleo">
                {feature.description}
              </p>
            </motion.div>)}
        </div>
      </div>

      {/* Closing transition — descend from light back into the deep */}
      <div aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-[200px] pointer-events-none" style={{
      background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, #0A1F2D 70%, #0A0D0C 100%)'
    }} data-id="element-206" data-mp="24g46pmgc9g3h" />
    </section>;
}
export function WhatMakesItDifferent() {
  const differences = [{
    icon: Compass,
    title: 'Conocimiento Local',
    description: 'Guiados por expertos que conocen cada corriente, cada cueva y el comportamiento de la fauna local.'
  }, {
    icon: Clock,
    title: 'Ritmo de Expedición',
    description: 'Sin prisas comerciales. Nos enfocamos en buscar la mejor luz, las mejores condiciones y el momento perfecto.'
  }, {
    icon: Anchor,
    title: 'Grupo Reducido',
    description: 'Máximo 12 personas. Una experiencia íntima que nos permite movernos con agilidad y flexibilidad total.'
  }];
  return <section className="py-24 md:py-32 bg-noronha-graphite/30 border-y border-noronha-petroleum/20" data-id="element-207" data-mp="17r66hoptkpyx">
      <div className="container mx-auto px-6" data-id="element-208" data-mp="zaxji94ee08t">
        <SectionHeader eyebrow="Nuestra Filosofía" title="¿Qué hace diferente este viaje?" description="No vendemos paquetes turísticos. Diseñamos expediciones para buceadores apasionados." className="mb-16" data-id="element-209" data-mp="v80u9zkcr5vw" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-210" data-mp="1464g6wz924o8">
          {differences.map((diff, idx) => <PremiumCard key={idx} delay={idx * 0.1} className="bg-noronha-volcanic" data-id="element-211" data-mp="bjjp7xmorul7" data-mpi={idx}>
              <diff.icon className="w-10 h-10 text-noronha-gold mb-6" strokeWidth={1.5} data-id="element-212" data-mp="q8xzd7y36g1n" />
              <h3 className="text-xl font-heading text-noronha-text-heading mb-3" data-id="element-213" data-mp="lfmrd3hjft85">
                {diff.title}
              </h3>
              <p className="text-noronha-text-body leading-relaxed" data-id="element-214" data-mp="3oyog93qsjfbe">
                {diff.description}
              </p>
            </PremiumCard>)}
        </div>
      </div>
    </section>;
}
export function TopReasons() {
  const reasons = [{
    num: '01',
    title: 'Paisaje Volcánico Submarino',
    desc: 'Cañones, cuevas y formaciones de lava solidificada únicas en el mundo.'
  }, {
    num: '02',
    title: 'Delfines Rotadores',
    desc: 'Encuentros cercanos con la colonia residente más grande del mundo.'
  }, {
    num: '03',
    title: 'Visibilidad Épica',
    desc: 'Aguas cristalinas que superan los 40 metros de visibilidad horizontal.'
  }, {
    num: '04',
    title: 'Naufragio Corveta V17',
    desc: 'Uno de los pecios más intactos y espectaculares del Atlántico Sur.'
  }, {
    num: '05',
    title: 'Desconexión Total',
    desc: 'Un entorno salvaje donde el ritmo lo marca la salida y puesta del sol.'
  }];
  return <section className="py-24 md:py-32 bg-noronha-volcanic" data-id="element-215" data-mp="2z9spyl6xnniq">
      <div className="container mx-auto px-6" data-id="element-216" data-mp="39erb10p1yt6l">
        <SectionHeader eyebrow="Highlights" title="Top 5 razones para viajar" className="mb-16" data-id="element-217" data-mp="39eiwz5h71htt" />

        <div className="flex flex-col gap-4 max-w-4xl mx-auto" data-id="element-218" data-mp="2zp7q7phs4p93">
          {reasons.map((reason, idx) => <PremiumCard key={idx} delay={idx * 0.1} className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-8 p-6 md:p-6" data-id="element-219" data-mp="2vnvx39zp5lw5" data-mpi={idx}>
              <span className="font-mono text-4xl md:text-5xl text-noronha-petroleum font-light" data-id="element-220" data-mp="2lppwqwtjgnte">
                {reason.num}
              </span>
              <div data-id="element-221" data-mp="2gqvf3y7b55av">
                <h3 className="text-xl font-heading text-noronha-text-heading mb-2" data-id="element-222" data-mp="2gsfzww3tl9vj">
                  {reason.title}
                </h3>
                <p className="text-noronha-text-body" data-id="element-223" data-mp="eziazbd5z1fv">{reason.desc}</p>
              </div>
            </PremiumCard>)}
        </div>
      </div>
    </section>;
}